import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { Popup } from '@/components';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { DoneOutline, Email } from '@mui/icons-material';
import { useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { PlutoClientException } from '@/PlutoClientException';
import { CommonPageContainer, CommonPageHeader } from '../common/CommonPageComponents.style';

export default function FeedbackForm({ closePopup }: { closePopup: () => Promise<void> }) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [showTemplate, setShowTemplate] = useState<boolean>();
  const [showError, setShowError] = useState<boolean>(false);
  const isOnlyWhitespace = !value || value.trim().length === 0;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  async function getSentEmailStatus(): Promise<AxiosResponse<boolean> | undefined> {
    const today = new Date();
    const params = new URLSearchParams();
    params.append('feedback', value);
    params.append('date', today.getTime().toString());
    const url = `/api/v1.1/feedback/sendFeedback?${params.toString()}`;

    return axios.get<boolean>(url);
  }

  async function onAction() {
    try {
      const result = await getSentEmailStatus();

      if (result?.data) {
        setShowTemplate(true);
      }
    } catch (err) {
      if (err instanceof PlutoClientException) {
        setShowError(true);
      }
    }
  }

  function SuccessTemplate() {
    setValue('');
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '20px',
          zIndex: 10,
          width: '99%',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              color: 'success.main',
            }}
            data-qa-element-id="thank_you_title"
          >
            {t('mainMenu.contactSubmenu.thankYou')}
          </Typography>

          <DoneOutline sx={{ fontSize: 40, color: 'success.main' }} />

          <Typography
            variant="body2"
            sx={{
              color: 'success.main',
              textAlign: 'center',
            }}
            data-qa-element-id="feedback_sent_label"
          >
            {t('mainMenu.contactSubmenu.successfulFeedback1')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'success.main',
              textAlign: 'center',
            }}
            data-qa-element-id="feedback_sent_label_2"
          >
            {t('mainMenu.contactSubmenu.successfulFeedback2')}
          </Typography>
        </Stack>
      </Box>
    );
  }

  const header = (
    <>
      <Stack spacing={2} sx={{ margin: '5px' }}>
        <Typography variant="h4" data-qa-element-id="text_us_title">
          {t('mainMenu.contactSubmenu.writeUs')}
        </Typography>

        <Typography variant="helperText" color="text.secondary" data-qa-element-id="text_us_description">
          {t('mainMenu.contactSubmenu.feedbackText')}
        </Typography>
      </Stack>
    </>
  );

  const body = (
    <>
      <Box position="relative">
        {showTemplate && <SuccessTemplate />}
        <TextField
          fullWidth
          multiline
          variant="outlined"
          size="small"
          rows={10}
          slotProps={{
            htmlInput: {
              style: {
                fontSize: '16px',
                transform: 'scale(0.875)',
                transformOrigin: 'left top',
                opacity: 1,
              },
            },
          }}
          placeholder={!showTemplate ? t('mainMenu.contactSubmenu.feedbackTextPlaceHolder') : ''}
          value={value}
          error={showError}
          onChange={handleChange}
          sx={{
            '& .MuiOutlinedInput-root': {
              pointerEvents: showTemplate ? 'none' : 'auto',
              fontSize: '1.2rem',
              borderRadius: '14px',
              marginTop: '10px',
              background: showTemplate ? '#FFFF' : '#0000000F',
              boxShadow: showError || showTemplate ? 'none' : '0 0 3px 3px rgba(33, 150, 243, 0.7)',

              '& fieldset': {
                borderColor: showError ? 'error.main' : showTemplate ? 'success.main' : 'info.main',
                borderWidth: 2,
              },

              '&:hover fieldset': {
                borderColor: showError ? 'error.main' : 'info.main',
              },

              '&.Mui-focused': {
                boxShadow: 'none',
                transition: '0.5s ease',
              },
            },
          }}
          data-qa-element-id="feedback_text_field"
        />
      </Box>

      <Button
        variant="contained"
        onClick={onAction}
        disabled={value.length < 1 || isOnlyWhitespace}
        sx={{
          mt: 2,
          width: '50%',
          ml: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontSize: '14px',
          padding: '6px 12px',
        }}
        data-qa-element-id="send_feedback_button"
      >
        <Email />
        {t('mainMenu.contactSubmenu.sendFeedback')}
      </Button>
    </>
  );

  return isPhoneOrHighScreen() ? (
    <Popup
      size={'sm'}
      header={header}
      body={body}
      footer={undefined}
      closePopup={closePopup}
      popupTitle={t('mainMenu.contactSubmenu.feedback')}
      isFullScr={isPhoneOrHighScreen() && true}
      leftText={true}
      hideBackdrop={isPhoneOrHighScreen() ? true : false}
      showCloseButton={true}
    />
  ) : (
    <CommonPageContainer data-qa-element-id="send_feedback">
      <CommonPageHeader data-qa-element-id="send_feedback_view_title">
        {t('mainMenu.contactSubmenu.feedback')}
      </CommonPageHeader>
      {header}
      {body}
    </CommonPageContainer>
  );
}
